import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import './d20-die.js';

/**
 * Initiative reveal banner. Matches the combat turn-banner style (red) and
 * animates the reveal of all combatants rolling for initiative:
 *
 *   t=0        : banner appears with main title "{initiator} is attacking {target}"
 *   t=500ms    : subtitle "Rolling initiative…" fades in
 *   t=1000ms   : first combatant row appears with d20 spinning
 *   t=1500ms   : that die settles on its rolled face; next combatant appears spinning
 *   t=2000ms   : ...and so on, 500ms between each combatant
 *   t=+1200ms  : after all settled, hold briefly, fade out, dispatch reveal-complete
 */
class InitiativeReveal extends BaseComponent {
  static properties = {
    combatState: { type: Object, attribute: false },
    currentUserId: { type: String, attribute: 'current-user-id' },
    _subtitleVisible: { type: Boolean, state: true },
    _revealedCount: { type: Number, state: true },
    _settledIds: { type: Object, state: true },
    _isLeaving: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .initiative-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      background: rgba(0, 0, 0, 0.55);
      animation: overlay-fade-in 0.3s ease-out;
    }

    .initiative-overlay.is-leaving {
      animation: overlay-fade-out 0.4s ease-in forwards;
    }

    .banner {
      background: rgba(185, 28, 28, 0.9);
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 12px;
      padding: 24px 40px;
      min-width: 420px;
      max-width: 640px;
      text-align: center;
      color: #fff;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    }

    .title {
      font-size: 32px;
      font-weight: bold;
      margin-bottom: 8px;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
      /* Honor explicit \n in server-rendered titles so multi-clause
         banners (e.g. "You discover a monster,\nhalting your
         movement!") wrap at the author-chosen break instead of an
         arbitrary point dictated by max-width. */
      white-space: pre-line;
    }

    .subtitle {
      font-size: 20px;
      color: rgba(255, 255, 255, 0.9);
      font-style: italic;
      margin-bottom: 20px;
      min-height: 1.2em;
      opacity: 0;
      transition: opacity 0.4s ease-in;
    }

    .subtitle.is-visible {
      opacity: 1;
    }

    .combatants {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: stretch;
    }

    .combatant {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 6px 12px;
      border-radius: 6px;
      background: rgba(30, 30, 40, 0.85);
      opacity: 0;
      transform: translateY(6px);
      animation: combatant-enter 0.3s ease-out forwards;
    }

    .combatant.is-pc {
      border-left: 3px solid #3b82f6;
    }

    .combatant.is-npc {
      border-left: 3px solid #ef4444;
    }

    .name {
      flex: 1;
      text-align: left;
      font-size: 15px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .modifier {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.75);
      min-width: 32px;
      text-align: center;
      opacity: 0;
      transition: opacity 0.3s ease-in;
      font-variant-numeric: tabular-nums;
    }

    .total {
      font-size: 18px;
      font-weight: 700;
      min-width: 28px;
      text-align: right;
      opacity: 0;
      transition: opacity 0.3s ease-in;
      font-variant-numeric: tabular-nums;
    }

    .modifier.is-visible,
    .total.is-visible {
      opacity: 1;
    }

    @keyframes overlay-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes overlay-fade-out {
      from { opacity: 1; }
      to { opacity: 0; }
    }

    @keyframes combatant-enter {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  constructor() {
    super();
    /** @type {object|null} */
    this.combatState = null;
    this.currentUserId = '';
    this._subtitleVisible = false;
    this._revealedCount = 0;
    /** @type {Set<string>} */
    this._settledIds = new Set();
    this._isLeaving = false;
    /** @type {ReturnType<typeof setTimeout>[]} */
    this._timers = [];
  }

  async connectedCallback() {
    super.connectedCallback();
    this._startSequence();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const t of this._timers) clearTimeout(t);
    this._timers = [];
  }

  _startSequence() {
    const order = this.combatState?.initiativeOrder ?? [];
    if (order.length === 0) {
      this.dispatchEvent(new CustomEvent('reveal-complete', { bubbles: true }));
      return;
    }

    const schedule = (delay, fn) => this._timers.push(setTimeout(fn, delay));

    // Timing (matches death save die feel):
    //   t=500ms  : subtitle "Rolling initiative…" fades in
    //   t=1000ms : first combatant row appears with die spinning
    //   t=1500ms : second combatant row appears with die spinning
    //   t=...    : 500ms between each additional combatant
    //   each die spins for 750ms after its row appears, then settles
    //   t=settle+2500ms : fade out and resolve
    const subtitleAt = 500;
    const firstRevealAt = 1000;
    const betweenCombatants = 500;
    const spinDuration = 750;
    const holdAfterLastSettle = 2500;

    schedule(subtitleAt, () => {
      this._subtitleVisible = true;
    });

    order.forEach((entry, i) => {
      const revealAt = firstRevealAt + i * betweenCombatants;
      schedule(revealAt, () => {
        this._revealedCount = i + 1;
      });
      schedule(revealAt + spinDuration, () => {
        this._settledIds = new Set([...this._settledIds, entry.actorId]);
      });
    });

    const lastSettleAt = firstRevealAt + (order.length - 1) * betweenCombatants + spinDuration;
    schedule(lastSettleAt + holdAfterLastSettle, () => {
      this._isLeaving = true;
    });
    schedule(lastSettleAt + holdAfterLastSettle + 400, () => {
      this.dispatchEvent(new CustomEvent('reveal-complete', { bubbles: true }));
    });
  }

  _getTitle() {
    // Server-rendered banners (#306): if the server emitted a banner
    // for this combat:started event, use it verbatim. The fan-out
    // already personalized "you" vs "{Ally}" for this recipient and
    // picked the right variant (game-start auto-trigger →
    // "There are monsters nearby! Rolling Initiative.").
    const cs = this.combatState;
    if (cs?.banner?.title) {
      return cs.banner.title;
    }
    if (!cs) return 'Combat!';

    // Fallback path: legacy local-builder for any combat:started event
    // that arrives without a server banner (replays of pre-#306 events,
    // events from a server that hasn't been migrated yet).
    const myId = this.currentUserId;
    const targetEntry = cs.initiativeOrder?.find((e) => e.actorId === cs.targetId);
    const initiatorEntry = cs.initiativeOrder?.find((e) => e.actorId === cs.initiatorId);

    if (cs.initiatorType === 'pc') {
      const attacker = initiatorEntry?.playerId === myId ? 'You' : cs.initiatorName || 'An ally';
      const target = cs.targetName || 'an enemy';
      return `${attacker} ${attacker === 'You' ? 'are' : 'is'} attacking ${target}!`;
    }

    if (cs.initiatorType === 'npc') {
      const attacker = cs.initiatorName || 'An enemy';
      const target = targetEntry?.playerId === myId ? 'you' : cs.targetName || 'an ally';
      return `${attacker} is attacking ${target}!`;
    }

    return 'Combat begins!';
  }

  _getSubtitle() {
    return this.combatState?.banner?.subtitle || 'Rolling initiative…';
  }

  render() {
    if (!this.combatState?.initiativeOrder) return html``;

    const order = this.combatState.initiativeOrder;
    const visibleEntries = order.slice(0, this._revealedCount);

    return html`
      <div class="initiative-overlay ${this._isLeaving ? 'is-leaving' : ''}">
        <div class="banner">
          <div class="title">${this._getTitle()}</div>
          <div class="subtitle ${this._subtitleVisible ? 'is-visible' : ''}">
            ${this._getSubtitle()}
          </div>
          <div class="combatants">
            ${visibleEntries.map((entry) => {
              const settled = this._settledIds.has(entry.actorId);
              const isPc = entry.actorType === 'pc';
              const finalFace = settled ? (entry.breakdown?.d20Roll ?? null) : null;
              const modifierSum = (entry.breakdown?.modifiers ?? []).reduce((sum, m) => sum + (m.value || 0), 0);
              const modifierText = modifierSum >= 0 ? `+${modifierSum}` : `${modifierSum}`;
              const total = entry.breakdown?.total ?? (entry.breakdown?.d20Roll ?? 0) + modifierSum;
              return html`
                <div class="combatant ${isPc ? 'is-pc' : 'is-npc'}">
                  <span class="name">${entry.name || entry.actorId}</span>
                  <d20-die
                    .spinning="${!settled}"
                    .finalFace="${finalFace}"
                  ></d20-die>
                  <span class="modifier ${settled ? 'is-visible' : ''}">
                    ${modifierText}
                  </span>
                  <span class="total ${settled ? 'is-visible' : ''}">
                    ${settled ? total : ''}
                  </span>
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('initiative-reveal', InitiativeReveal);
